/**
 * siteBeacon.js — visitor / session beacon for the Traffic & Security console.
 *
 * DISTINCT from analytics.js: that util is browse-funnel analytics gated on
 * `guild.consent.analytics`. This one runs on a security/legitimate-interest
 * basis and is therefore NOT consent-gated — every visit is tracked so admins
 * can see who is on the site and detect abuse. (The privacy policy must
 * disclose security-purpose IP + session logging.)
 *
 * We never send a uid from the client: the Firebase callable automatically
 * attaches the signed-in user's auth token, and the server reads `auth.uid`.
 * So simply firing a hit after login associates the anonymous visitor/session
 * with the now-authenticated user — no client-supplied identity to forge.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";

const VISITOR_KEY = "guild.vid";
const SESSION_KEY = "guild.sid";
const SESSION_TS_KEY = "guild.sid.ts";
// Keep this aligned with SESSION_IDLE_MS on the server (siteSecurityFn.js).
const SESSION_IDLE_MS = 15 * 60 * 1000;

function safeUUID() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* ignore */
  }
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readLS(key) {
  try {
    return localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
}

function writeLS(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_e) {
    /* storage unavailable — beacon degrades to per-call ids */
  }
}

/** Persistent visitor id, stable across sessions and login/logout. */
export function getVisitorId() {
  let id = readLS(VISITOR_KEY);
  if (!id) {
    id = safeUUID();
    writeLS(VISITOR_KEY, id);
  }
  return id;
}

/**
 * Current session id, rotated after SESSION_IDLE_MS of inactivity. Reading it
 * also refreshes the last-active stamp, so an active tab keeps one session.
 */
export function getSessionId() {
  const now = Date.now();
  const lastTs = Number(readLS(SESSION_TS_KEY)) || 0;
  let sid = readLS(SESSION_KEY);
  if (!sid || now - lastTs > SESSION_IDLE_MS) {
    sid = safeUUID();
    writeLS(SESSION_KEY, sid);
  }
  writeLS(SESSION_TS_KEY, String(now));
  return sid;
}

let cachedCallable = null;
function getCallable() {
  if (cachedCallable) return cachedCallable;
  try {
    cachedCallable = httpsCallable(functions, "recordSiteHit");
  } catch (_e) {
    cachedCallable = null;
  }
  return cachedCallable;
}

function screenInfo() {
  try {
    return {
      w: window.screen?.width || null,
      h: window.screen?.height || null,
      dpr: window.devicePixelRatio || null,
    };
  } catch (_e) {
    return null;
  }
}

/**
 * Record a single page hit. Always safe to call — never throws, never blocks
 * navigation. Returns the server result (incl. `{ blocked }`, used in Phase 4)
 * or null on any failure.
 *
 * @param {string} path  Route path (pathname + search).
 * @returns {Promise<{ok?:boolean, blocked?:boolean}|null>}
 */
export async function recordHit(path) {
  const call = getCallable();
  if (!call) return null;

  let referrer = null;
  try {
    referrer = document.referrer || null;
  } catch (_e) {
    /* ignore */
  }

  const payload = {
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    path: String(path || "/").slice(0, 512),
    referrer,
    screen: screenInfo(),
    tzOffset: new Date().getTimezoneOffset(),
  };

  try {
    const res = await call(payload);
    return (res && res.data) || null;
  } catch (_e) {
    // Tracking must never break the visitor's experience.
    return null;
  }
}

export default recordHit;
